import { Fragment, useState, useMemo, useEffect, type FormEvent } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useExtensions, useCreateExtension, useDeleteExtension, useCreateExtensionRange,
  type RangeResult,
} from '../../lib/api/hooks.js';
import { useAdminGroups } from '../../lib/api/hooks-extension.js';
import { useRealtime } from '../../lib/api/realtime.js';
import {
  StatusBadge, TableSkeleton, Empty, ErrorBanner, Badge, Segmented, Tabs, Tab,
} from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { Switch } from '../../components/ui/Form.js';
import { ApiClientError } from '../../lib/api/client.js';
import { useExtensionTypes, useTypeFeatures } from '../../lib/api/hooks-products.js';
import { RangeCredentials } from './RangeCredentials.js';
import { PresenceCell, presenceOf } from './ExtensionPresence.js';
import { SeatPhones } from './SeatPhones.js';
import { ExtensionDetail, type SeatLink } from '../manage/ExtensionDetail.js';
import { useSubroute, childSubroute, SubrouteProvider } from '../../lib/subroute.js';
import type { Extension } from '@arcnet/contracts';

/**
 * The seat list.
 *
 * Rebuilt around the question people actually open it with, which is not "what
 * is extension 1004's caller ID" — it is "who is online, and who should be but
 * isn't". Presence therefore leads, and the columns that used to fill the
 * width (caller ID, max concurrent calls) moved to the seat's own page, where
 * somebody looking for them is already going.
 *
 * One list, then one seat (14 September 2026). This was three columns -- a
 * filter rail, the seats, and the open seat in whatever width was left -- under
 * a row of counters that repeated the rail's own filters. The owner found it
 * hard to find their way around, and the seat's twelve tabs did not fit its
 * third of the screen, so most of them were out of sight. Now the counts are
 * the filters, and a seat opens on a page of its own with every section listed.
 *
 * Reused in two places: standalone, and as a tab inside an account. `tenantId`
 * is passed explicitly when embedded so the tab always shows the account in the
 * URL rather than whatever tenant happens to be selected elsewhere — otherwise
 * a support link could open the wrong customer's data.
 */

type Presence = 'all' | 'online' | 'offline' | 'never' | 'forwarded' | 'dnd';

/**
 * Who to show, by what is happening to their calls. Forwarded and DND are here
 * because the account-wide forwarding list went when forwarding moved onto the
 * extension (B4) -- "who is forwarded somewhere" is still a question worth one
 * click, and now it is a filter on the seats rather than a screen of its own.
 */
const PRESENCE: ReadonlyArray<readonly [Presence, string]> = [
  ['all', 'All'], ['online', 'Online'], ['offline', 'Offline'],
  ['never', 'Never seen'], ['forwarded', 'Forwarded'], ['dnd', 'Do not disturb'],
];

const isForwarded = (e: Extension) => !!(e.forward_always || e.forward_busy || e.forward_no_answer);

/** Unassigned is a filter value that cannot collide with a group id. */
const UNASSIGNED = '__unassigned__';

const seatLink = (e: Extension | undefined): SeatLink | undefined =>
  e ? { key: e.number, label: `${e.number} · ${e.display_name}` } : undefined;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function ExtensionsPage({ tenantId: propTenantId, embedded, scope = 'tenant' }: {
  tenantId?: string;
  embedded?: boolean;
  /**
   * Which console this list is inside. The MSP account record shows a seat's
   * identity and feature set; how the customer runs it day to day lives in
   * their own portal. See VIEWS in ExtensionDetail.
   */
  scope?: 'platform' | 'tenant';
} = {}) {
  const activeTenantId = useSession((s) => s.activeTenantId);
  const tenantId = propTenantId ?? activeTenantId;

  /**
   * The open seat lives in the URL, by its NUMBER: .../extensions/1001, and
   * .../extensions/1001/phone for one of its sections.
   *
   * It was component state, which meant no link could name a seat. Then it was
   * `?ext=<uuid>`, which named the seat in 36 characters nobody can read or say
   * (14 September 2026). A number is what people call a seat, and it is unique
   * within the account the path already names. An id still opens the seat, and
   * is rewritten to its number as soon as that is known.
   */
  const route = useSubroute();
  const seatKey = route.rest[0] ?? null;
  const view = route.rest[1];
  /**
   * `list` opens a seat at Details. `step` moves to the seat above or below and
   * keeps the section, so Phone setup can be checked seat after seat.
   */
  const openSeat = (number: string | null, how: 'list' | 'step' = 'list'): void => {
    // Opening a seat is navigation the back button should undo. Closing one
    // and stepping between them replace, so back returns to the list rather
    // than walking through every seat that was looked at.
    if (number === null) { route.go([], { replace: true }); return; }
    route.go(how === 'step' && view ? [number, view] : [number], { replace: how === 'step' });
  };
  const can = useSession((s) => s.can);
  const [q, setQ] = useState('');
  const [presence, setPresence] = useState<Presence>('all');
  /** The seat whose phones are open, if any. */
  const [phones, setPhones] = useState<string | null>(null);
  const [group, setGroup] = useState('');
  const [adding, setAdding] = useState(false);

  // Live for as long as the list is open: a phone registering moves a row from
  // offline to online in about a second, without a refresh.
  const live = useRealtime(tenantId ?? null);
  const { data, isLoading, error } = useExtensions(tenantId, { q: q || undefined, limit: 200 });
  const groups = useAdminGroups(tenantId ?? '');
  const del = useDeleteExtension(tenantId ?? '');

  const all = useMemo(() => data?.data ?? [], [data]);
  /**
   * The three presence states PARTITION the seats: online + offline + never
   * seen = all.
   *
   * `offline` used to be `total - online`, which swallowed the never-seen
   * seats, so two seats displayed as 0 online, 2 offline and 1 never seen --
   * three numbers for two seats. A seat is a handset that stopped or one that
   * was never configured, never both. Forwarded and DND cut across all three.
   */
  const counts = useMemo(() => {
    const c: Record<Presence, number> = {
      all: all.length, online: 0, offline: 0, never: 0, forwarded: 0, dnd: 0,
    };
    for (const e of all) {
      c[presenceOf(e).state]++;
      if (isForwarded(e)) c.forwarded++;
      if (e.do_not_disturb) c.dnd++;
    }
    return c;
  }, [all]);

  const rows = useMemo(() => all.filter((e) => {
    // The same three states as the counts, so "Offline" returns the number its
    // own count shows rather than that plus the never-seen seats.
    if ((presence === 'online' || presence === 'offline' || presence === 'never')
      && presenceOf(e).state !== presence) return false;
    if (presence === 'forwarded' && !isForwarded(e)) return false;
    if (presence === 'dnd' && !e.do_not_disturb) return false;
    if (group === UNASSIGNED && e.admin_group_id) return false;
    if (group && group !== UNASSIGNED && e.admin_group_id !== group) return false;
    return true;
  }), [all, presence, group]);

  /**
   * Which seat the address names. Usually it is on the page already. One that
   * is not -- a search is typed, or the account has more seats than the page
   * holds -- is looked up by its number.
   */
  const byId = seatKey !== null && UUID.test(seatKey);
  const listed = seatKey === null ? undefined
    : all.find((e) => (byId ? e.id : e.number) === seatKey);
  const lookup = useExtensions(
    seatKey !== null && !byId && !listed && !isLoading ? tenantId ?? null : null,
    { q: seatKey ?? undefined, limit: 20 },
  );
  const seat = listed ?? lookup.data?.data.find((e) => e.number === seatKey);
  const seatId = byId ? seatKey : seat?.id ?? null;
  useEffect(() => {
    if (byId && seat) route.go([seat.number, ...route.rest.slice(1)], { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byId, seat?.number]);

  if (!tenantId) {
    return <Empty icon="accounts" title="No tenant selected"
      hint="Choose a tenant to manage its extensions." />;
  }

  /**
   * A seat replaces the list, at every width.
   *
   * Beside the list it had a third of the screen, which is how its sections
   * came to be out of sight. Stepping through seats -- the one thing the
   * side-by-side layout did well -- is the up and down buttons on the seat,
   * following the list as it was filtered.
   */
  if (seatKey !== null) {
    if (!seatId) {
      return (
        <>
          <button type="button" className="arc-crumb" onClick={() => openSeat(null)}
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
            <Icon name="arrowLeft" size={15} /> Extensions
          </button>
          {isLoading || lookup.data === undefined ? (
            <div className="arc-card arc-card-body">
              <div className="arc-skeleton" style={{ height: 90 }} />
            </div>
          ) : (
            <div className="arc-card">
              <Empty icon="extension" title={`No extension ${seatKey}`}
                hint="It may have been renumbered or removed." />
            </div>
          )}
        </>
      );
    }
    const order = rows.some((e) => e.id === seatId) ? rows : all;
    const at = order.findIndex((e) => e.id === seatId);
    return (
      // Below the seat is the seat's own: which of its sections is open.
      <SubrouteProvider value={childSubroute(route, [seatKey])}>
        <ExtensionDetail tenantId={tenantId} extensionId={seatId} scope={scope}
          onBack={() => openSeat(null)}
          prev={at > 0 ? seatLink(order[at - 1]) : undefined}
          next={at >= 0 ? seatLink(order[at + 1]) : undefined}
          onStep={(number) => openSeat(number, 'step')} />
      </SubrouteProvider>
    );
  }

  const groupList = groups.data?.data ?? [];
  // A cost-centre column reading "unassigned" on every row says nothing, so
  // the column and its filter appear only once the account has cost centres.
  const hasGroups = groupList.length > 0;

  return (
    <>
      <div className="arc-page-head">
        <div>
          {!embedded && <h1 className="arc-h1">Extensions</h1>}
          {!embedded && <p>Seats, their handsets and who is online</p>}
        </div>
        <div className="arc-row">
          {live === 'live' && (
            <Badge tone="ok"><span className="arc-dot" aria-hidden="true" />live</Badge>
          )}
          {can('extension:write') && (
            <button type="button" className="arc-btn arc-btn-primary"
              onClick={() => setAdding((v) => !v)}>
              <Icon name={adding ? 'close' : 'plus'} size={13} />
              {adding ? 'Cancel' : 'New extension'}
            </button>
          )}
        </div>
      </div>

      {error && <ErrorBanner error={error} />}
      {adding && <NewExtensionForm tenantId={tenantId} onDone={() => setAdding(false)} />}

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <Icon name="search" size={14} />
            <input className="arc-input" placeholder="Search number or name…"
              value={q} onChange={(e) => setQ(e.target.value)}
              aria-label="Search extensions" />
          </div>

          {/* The counts are the filters. A row of counters above a rail of the
              same filters said everything twice, in two places, at two sizes. */}
          <Segmented label="Show" value={presence} onChange={setPresence}
            options={PRESENCE.map(([k, label]) => [k, label, counts[k]] as const)} />

          {hasGroups && (
            <select className="arc-input" style={{ maxWidth: 190 }} value={group}
              aria-label="Filter by department"
              onChange={(e) => setGroup(e.target.value)}>
              <option value="">All departments</option>
              <option value={UNASSIGNED}>Unassigned</option>
              {groupList.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          )}

          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {rows.length === all.length ? `${all.length}` : `${rows.length} of ${all.length}`} shown
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            {/* Widths declared rather than left to the browser.
                Auto layout shares the slack out evenly, so a dash ends up as
                wide as the names -- and the name is the thing being scanned
                for. Everything the eye does not search on is pinned to what it
                needs; the extension column absorbs the remainder. */}
            <colgroup>
              <col />
              {/* 190px because the widest status cell measures 176 in the
                  browser -- "never seen · no handset yet" -- and anything
                  narrower wraps it, making that row taller than its
                  neighbours. */}
              <col style={{ width: 190 }} />
              <col style={{ width: '16%' }} />
              {/* An IPv4 address in the mono face; anything longer truncates. */}
              <col style={{ width: 132 }} />
              {hasGroups && <col style={{ width: '14%' }} />}
              <col style={{ width: '16%' }} />
              <col style={{ width: 80 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ width: 30 }} aria-label="Phones" />
                <th>Extension</th>
                <th>Status</th>
                <th>Phone</th>
                <th>IP</th>
                {hasGroups && <th>Department</th>}
                <th>Type</th>
                <th />
              </tr>
            </thead>
            {isLoading ? <TableSkeleton cols={hasGroups ? 8 : 7} /> : (
              <tbody>
                {rows.map((e) => (
                  <Fragment key={e.id}>
                  <tr className="arc-clickable" onClick={() => openSeat(e.number)}>
                    {/* The seat's phones, under the seat. Its badge is about the line it answers
                        as; a seat can have three registrations and they are not the same thing. */}
                    <td>
                      <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                        aria-expanded={phones === e.id}
                        title={phones === e.id ? 'Hide this extension’s phones' : 'Show this extension’s phones'}
                        aria-label={phones === e.id ? `Hide ${e.number}’s phones` : `Show ${e.number}’s phones`}
                        onClick={(evt) => { evt.stopPropagation(); setPhones(phones === e.id ? null : e.id); }}>
                        <Icon name={phones === e.id ? 'chevronDown' : 'chevronRight'} size={13} />
                      </button>
                    </td>
                    <td>
                      <div className="arc-row" style={{ gap: 10 }}>
                        <span className="arc-ext-num">{e.number}</span>
                        <div className="arc-stack">
                          <span style={{ fontWeight: 550 }}>{e.display_name}</span>
                          {e.status !== 'active' && <StatusBadge status={e.status} />}
                        </div>
                      </div>
                    </td>
                    <td><PresenceCell ext={e} /></td>
                    <td className="arc-muted">
                      {e.registration?.user_agent
                        ? <span className="arc-truncate" title={e.registration.user_agent}>
                            {e.registration.user_agent}
                          </span>
                        : <span className="arc-subtle">—</span>}
                    </td>
                    {/* Where the phone registered from, in a column of its own
                        (owner, 15 September 2026). */}
                    <td className="arc-muted">
                      {e.registration?.contact_ip
                        ? <span className="arc-truncate arc-mono" title={e.registration.contact_ip}>
                            {e.registration.contact_ip}
                          </span>
                        : <span className="arc-subtle">—</span>}
                    </td>
                    {hasGroups && (
                      <td className="arc-muted">
                        {e.admin_group_name ?? <span className="arc-subtle">unassigned</span>}
                      </td>
                    )}
                    {/* The package, which is what decides the features and the
                        monthly charge. DND and forwarding are filters above. */}
                    <td className="arc-muted">
                      {e.extension_type_name
                        ? <span className="arc-truncate">{e.extension_type_name}</span>
                        : <span className="arc-subtle">—</span>}
                    </td>
                    {/* Icons rather than the words "Open" and "Delete".
                        Two text buttons on every row put a second, competing
                        column of writing beside the names, and the names are
                        what the eye is scanning for. The controls stay
                        permanently visible -- only their labels moved to the
                        title and aria-label. */}
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions"
                        style={{ justifyContent: 'flex-end', gap: 2 }}>
                        {can('extension:delete') && (
                          <button type="button"
                            className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-danger-ghost"
                            title={`Delete extension ${e.number}`}
                            aria-label={`Delete extension ${e.number}`}
                            onClick={(evt) => {
                              evt.stopPropagation();
                              if (confirm(`Delete extension ${e.number} (${e.display_name})? The handset will stop working immediately.`)) {
                                del.mutate(e.id);
                              }
                            }}>
                            <Icon name="trash" size={15} />
                          </button>
                        )}
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                          title={`Open ${e.number}`} aria-label={`Open extension ${e.number}`}
                          onClick={(evt) => { evt.stopPropagation(); openSeat(e.number); }}>
                          <Icon name="chevronRight" size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {phones === e.id && (
                    <tr>
                      <td colSpan={hasGroups ? 8 : 7} style={{ padding: 0, background: 'var(--arc-surface-2)' }}>
                        <SeatPhones tenantId={tenantId} extensionId={e.id} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {data !== undefined && all.length === 0 && (
          <Empty icon="extension" title="No extensions" hint="Add one to start making calls." />
        )}
        {data !== undefined && all.length > 0 && rows.length === 0 && (
          <Empty icon="search" title="Nothing matches those filters"
            hint={hasGroups
              ? 'Choose All or clear the department filter to see the rest.'
              : 'Choose All to see the rest.'} />
        )}
      </div>
    </>
  );
}

/**
 * Creation, with the fields that are awkward to change later.
 *
 * Not every setting — a seat has a dozen sections of them and a wall of inputs
 * at creation is how people stop reading. These are the ones whose default is
 * wrong often enough to be worth asking: cost centre because unallocated spend
 * is what finance chases, and internal-only because the safe answer for a new
 * seat is "cannot dial out until somebody says so".
 */
function NewExtensionForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const create = useCreateExtension(tenantId);
  const createRange = useCreateExtensionRange(tenantId);
  const groups = useAdminGroups(tenantId);
  const [number, setNumber] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [internalOnly, setInternalOnly] = useState(false);
  const [typeId, setTypeId] = useState('');
  // Only the provider's staff choose a package. A customer's new seats get the
  // base package, applied by the API, so the picker is not offered to them.
  const can = useSession((s) => s.can);
  const canPackage = can('extension:package:write');
  /**
   * Single or a block. One form with a mode rather than two screens: every
   * field except the number means the same thing in both, and a second form
   * would be a second place for the type picker to drift.
   */
  const [mode, setMode] = useState<'single' | 'range'>('single');
  const [rangeTo, setRangeTo] = useState('');
  const [namePrefix, setNamePrefix] = useState('');
  const [rangeResult, setRangeResult] = useState<RangeResult | null>(null);
  const types = useExtensionTypes();
  const typeFeatures = useTypeFeatures(typeId || null);
  const [issued, setIssued] = useState<
    { username: string; realm: string; password: string } | null>(null);

  const submitRange = (e: FormEvent) => {
    e.preventDefault();
    createRange.mutate(
      {
        from: number, to: rangeTo,
        ...(namePrefix ? { display_name_prefix: namePrefix } : {}),
        ...(typeId && canPackage ? { extension_type_id: typeId } : {}),
        ...(groupId ? { admin_group_id: groupId } : {}),
        ...(internalOnly ? { internal_only: true } : {}),
      },
      {
        onSuccess: (r) => {
          setNumber(''); setRangeTo(''); setNamePrefix('');
          // Replaces the form: every password in it is visible exactly once.
          setRangeResult(r);
        },
      },
    );
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        number, display_name: displayName,
        ...(groupId ? { admin_group_id: groupId } : {}),
        ...(typeId && canPackage ? { extension_type_id: typeId } : {}),
        ...(internalOnly ? { internal_only: true } : {}),
      },
      {
        onSuccess: (created) => {
          setNumber(''); setDisplayName(''); setGroupId('');
          // The TYPE is deliberately kept. Seats are created in runs of the
          // same kind -- five agents, then three managers -- so re-picking it
          // for every one is the fiddly part of adding a team.
          /**
           * The form stays open when a password came back.
           *
           * Closing on success is right for everything else, and wrong here:
           * this is the only moment the SIP password is visible, and a dialog
           * that vanishes with it takes the handset's configuration with it.
           */
          if (created?.sip_credential) setIssued(created.sip_credential);
          else onDone();
        },
      },
    );
  };

  /**
   * Whichever mutation is in play. A range refusal is the MORE important of the
   * two to surface -- it names the numbers that collided, and that message is
   * the entire instruction for what to do next.
   */
  /**
   * Shown while typing so a mistyped range is obvious BEFORE submitting -- the
   * difference between 1010-1029 and 1010-10029 is one keystroke and two orders
   * of magnitude.
   */
  const rangeCount = /^\d{2,6}$/.test(number) && /^\d{2,6}$/.test(rangeTo)
    ? Math.max(0, Number(rangeTo) - Number(number) + 1)
    : 0;

  const active = mode === 'range' ? createRange : create;
  const fieldErrors = active.error instanceof ApiClientError ? active.error.fieldErrors : {};

  /**
   * Shown once, in place of the form.
   *
   * A SIP password is minted when the seat is created and can only be produced
   * again under step-up, so this is the moment it has to be captured. It
   * replaces the form rather than sitting beside it: the next thing to do is
   * put these three values into a handset, not create another extension.
   */
  if (rangeResult) {
    return (
      <RangeCredentials
        result={rangeResult}
        onDone={() => { setRangeResult(null); onDone(); }}
      />
    );
  }

  if (issued) {
    return (
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <h2 className="arc-h2" style={{ marginBottom: 4 }}>Extension created</h2>
        <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 0 }}>
          These are the credentials for the handset. The password is shown once and
          cannot be read back afterwards — copy it now.
        </p>
        <dl className="arc-kv" style={{ marginTop: 12 }}>
          <dt>SIP username</dt><dd className="arc-mono">{issued.username}</dd>
          <dt>Domain / realm</dt><dd className="arc-mono">{issued.realm}</dd>
          <dt>Password</dt>
          <dd className="arc-row" style={{ gap: 8 }}>
            <span className="arc-mono" style={{ fontWeight: 600, userSelect: 'all' }}>
              {issued.password}
            </span>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              onClick={() => void navigator.clipboard?.writeText(issued.password)}>
              Copy
            </button>
          </dd>
          {/* The registrar is OpenSIPS, not FreeSWITCH: the switch has no public
              listener at all (ADR-003), so a handset pointed at it never
              registers. Naming it here saves that discovery. */}
          <dt>SIP server</dt><dd className="arc-mono">{issued.realm}</dd>
        </dl>
        <div className="arc-row" style={{ gap: 8, marginTop: 14 }}>
          <button type="button" className="arc-btn arc-btn-primary"
            onClick={() => { setIssued(null); onDone(); }}>
            Done
          </button>
          <button type="button" className="arc-btn arc-btn-ghost"
            onClick={() => setIssued(null)}>
            Add another
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={mode === 'range' ? submitRange : submit}>
      <div className="arc-row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 className="arc-h2" style={{ margin: 0 }}>
          {mode === 'range' ? 'New extension range' : 'New extension'}
        </h2>
        <Tabs style={{ margin: 0, borderBottom: 'none' }}>
          <Tab active={mode === 'single'} onSelect={() => setMode('single')}>Single</Tab>
          <Tab active={mode === 'range'} onSelect={() => setMode('range')}>Range</Tab>
        </Tabs>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: mode === 'range'
          ? '110px 110px minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)'
          : '120px minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr)',
        gap: 12, alignItems: 'start',
      }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ext-number">
            {mode === 'range' ? 'From' : 'Number'}
          </label>
          <input id="ext-number" className="arc-input arc-mono" inputMode="numeric"
            placeholder={mode === 'range' ? '1010' : '1003'} required value={number}
            onChange={(e) => setNumber(e.target.value)}
            aria-invalid={!!fieldErrors['number'] || !!fieldErrors['from']} />
          {fieldErrors['number'] ?? fieldErrors['from']
            ? <div className="arc-error">{fieldErrors['number'] ?? fieldErrors['from']}</div>
            : <div className="arc-hint">
                2–6 digits. 91xx is reserved.
                {mode === 'range' && ' Leading zeros are kept.'}
              </div>}
        </div>

        {mode === 'range' ? (
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="ext-to">To</label>
            <input id="ext-to" className="arc-input arc-mono" inputMode="numeric"
              placeholder="1029" required value={rangeTo}
              onChange={(e) => setRangeTo(e.target.value)}
              aria-invalid={!!fieldErrors['to']} />
            {fieldErrors['to']
              ? <div className="arc-error">{fieldErrors['to']}</div>
              : <div className="arc-hint">
                  {rangeCount > 0
                    ? `${rangeCount} extension${rangeCount === 1 ? '' : 's'}. Up to 100 at once.`
                    : 'Inclusive. Up to 100 at once.'}
                </div>}
          </div>
        ) : (
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="ext-name">Display name</label>
            <input id="ext-name" className="arc-input" placeholder="Reception"
              required value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              aria-invalid={!!fieldErrors['display_name']} />
            {fieldErrors['display_name'] && <div className="arc-error">{fieldErrors['display_name']}</div>}
          </div>
        )}

        {mode === 'range' && (
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="ext-prefix">Name prefix</label>
            <input id="ext-prefix" className="arc-input" placeholder="Agent"
              value={namePrefix} onChange={(e) => setNamePrefix(e.target.value)} />
            <div className="arc-hint">
              {namePrefix
                ? `Names become "${namePrefix} ${number || '1010'}".`
                : 'Optional. Names default to the number itself.'}
            </div>
          </div>
        )}

        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ext-type">Extension type</label>
          {canPackage ? (
            <>
              <select id="ext-type" className="arc-input" value={typeId}
                onChange={(e) => setTypeId(e.target.value)}>
                <option value="">Base (default)</option>
                {types.data?.data.filter((t) => t.is_active).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <div className="arc-hint">
                {typeId
                  ? 'Loads the features below. Paid add-ons can be switched on per seat afterwards.'
                  : 'New seats get the Base package unless you choose another.'}
              </div>
            </>
          ) : (
            <div id="ext-type" className="arc-hint">
              New seats get the Base package. Your provider sets packages, so ask them to change one.
            </div>
          )}
        </div>

        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ext-group">Department</label>
          <select id="ext-group" className="arc-input" value={groupId}
            onChange={(e) => setGroupId(e.target.value)}>
            <option value="">Unassigned</option>
            {groups.data?.data.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          <div className="arc-hint">Where this seat's spend is reported.</div>
        </div>
      </div>

      {/*
        What the chosen type actually brings, shown BEFORE the seat is created.
        The type is a product decision with a price attached, and picking one
        from a list of names alone means finding out what was bought afterwards.
      */}
      {typeId && (typeFeatures.data?.data.some((f) => f.from_type) ?? false) && (
        <div style={{ marginTop: 14 }}>
          <div className="arc-label" style={{ marginBottom: 6 }}>
            Included with this type
          </div>
          <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {typeFeatures.data?.data.filter((f) => f.from_type).map((f) => (
              <span key={f.key} className="arc-badge arc-badge-muted" title={f.description}>
                <Icon name="check" size={10} /> {f.name}
              </span>
            ))}
          </div>
          <div className="arc-hint" style={{ marginTop: 6 }}>
            These come from the type, so changing the product later reaches this
            seat too. Overriding one here affects only this extension.
          </div>
        </div>
      )}

      <div className="arc-row" style={{ gap: 10, margin: '12px 0 14px' }}>
        <Switch id="ext-internal" checked={internalOnly} onChange={setInternalOnly} />
        <label htmlFor="ext-internal" style={{ fontSize: 12.5, cursor: 'pointer' }}>
          Internal calls only
          <span className="arc-muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
            No outside line until somebody turns it on. Everything else has a
            sensible default and lives on the seat's own page.
          </span>
        </label>
      </div>

      <button type="submit" className="arc-btn arc-btn-primary" disabled={active.isPending}>
        {active.isPending
          ? 'Creating…'
          : mode === 'range'
            ? `Create ${rangeCount > 0 ? rangeCount : ''} extension${rangeCount === 1 ? '' : 's'}`.replace('  ', ' ')
            : 'Create extension'}
      </button>
    </form>
  );
}
